import React, { memo, useCallback, useEffect } from "react";
import {
  Button,
  Dropdown,
  DatePicker,
  Badge,
  TreeSelect,
  Form,
  Checkbox,
  Card,
  Flex,
  Select,
  Tooltip,
} from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { useBoolean } from "hooks/use-boolean";
import dayjs, { Dayjs } from "dayjs";
import { lateTypeOptions } from "utils/constants";

const { TreeNode } = TreeSelect;
const { MonthPicker } = DatePicker;

interface Department {
  id: string;
  name: string;
  children?: Department[];
}

interface FilterProps {
  isEmpty: boolean;
  departments: Department[];
  onFilterChange: (filters: any) => void;
  isAdmin: boolean;
}

const mapTreeData = (nodes: any) => {
  return nodes.map((node: any) => (
    <TreeNode key={node.id} title={node.name} value={node.id}>
      {node.children && mapTreeData(node.children)}
    </TreeNode>
  ));
};

const Filter: React.FC<FilterProps> = ({
  isEmpty,
  onFilterChange,
  departments: departmentItems,
  isAdmin,
}) => {
  const [form] = Form.useForm();
  const open = useBoolean();

  const departments = departmentItems;

  const date = Form.useWatch("date", form);

  const getFilterFromStorage = useCallback(() => {
    const storedFilter = localStorage.getItem("WORKING_HOURS_STORAGE");

    if (storedFilter) {
      try {
        const filter = JSON.parse(storedFilter);
        onFilterChange(filter);

        const defaultValues = {
          date:
            filter.year && filter.month
              ? dayjs()
                  .year(filter.year)
                  .month(filter.month - 1)
              : null,
          day: filter.day ? dayjs().date(filter.day) : null,
          types: filter.types || [],
          departmentIds: filter.departmentIds || null,
          clearCache: filter.clearCache || false,
          showResignEmployee: filter.showResignEmployee || false,
        };

        form.setFieldsValue(defaultValues);
      } catch (error) {
        console.error("Failed to parse filter from storage:", error);
      }
    }
  }, [form, onFilterChange]);

  const handleClearFilter = useCallback(() => {
    open.onFalse();
    form.resetFields();
    localStorage.removeItem("WORKING_HOURS_STORAGE");
    onFilterChange({});
  }, [open, form, onFilterChange]);

  const handleApplyFilters = useCallback(() => {
    const values = form.getFieldsValue();
    const year = values.date ? values.date.year() : new Date().getFullYear();
    const month = values.date ? values.date.month() + 1 : 1;
    const day = values.day && values.day.date();

    const filter = {
      year,
      month,
      day,
      types: values.types || [],
      departmentIds: values.departmentIds || null,
      clearCache: values.clearCache || false,
      showResignEmployee: values.showResignEmployee || false,
    };

    localStorage.setItem("WORKING_HOURS_STORAGE", JSON.stringify(filter));
    onFilterChange(filter);
    open.onFalse();
  }, [form, onFilterChange, open]);

  const disabledDate = (current: Dayjs | null): boolean => {
    if (!current || !date) return true;
    return current.year() !== date.year() || current.month() !== date.month();
  };

  const dropdownContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleApplyFilters}
      initialValues={{
        clearCache: false,
        showResignEmployee: false,
        date: dayjs(),
      }}
    >
      <Card style={{ width: "400px" }}>
        {isAdmin && (
          <Form.Item
            label="Department"
            name="departmentIds"
            rules={[{ required: true, message: "Department is required" }]}
          >
            <TreeSelect
              allowClear
              showCheckedStrategy={TreeSelect.SHOW_ALL}
              treeCheckable={true}
              multiple
              maxTagCount={2}
              listHeight={400}
              treeDefaultExpandedKeys={["1"]}
              placeholder="Choose departments!"
              style={{ width: "100%" }}
            >
              {mapTreeData(departments)}
            </TreeSelect>
          </Form.Item>
        )}
        <Form.Item
          label="Select Month"
          name="date"
          rules={[{ required: true, message: "Month is required" }]}
        >
          <MonthPicker
            placeholder="Select Month and Year"
            format="YYYY-MM"
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Flex gap={10}>
          <Form.Item label="Select Day" name="day">
            <DatePicker
              placeholder="Day"
              disabled={!date}
              format={(value) => (value ? value.format("DD") : "")}
              picker="date"
              disabledDate={disabledDate}
            />
          </Form.Item>
          <Form.Item label="Select type" name="types">
            <Select
              options={lateTypeOptions}
              style={{ width: 200 }}
              allowClear
              mode="multiple"
            />
          </Form.Item>
        </Flex>

        <Form.Item name="clearCache" valuePropName="checked">
          <Checkbox>Clear Cache</Checkbox>
        </Form.Item>
        {isAdmin && (
          <Form.Item name="showResignEmployee" valuePropName="checked">
            <Checkbox>Show Resigned Employees</Checkbox>
          </Form.Item>
        )}
        <div className="flex gap-3">
          <Button
            onClick={handleClearFilter}
            variant="filled"
            color="danger"
            className="flex-1"
          >
            Clear filter
          </Button>
          <Button type="primary" htmlType="submit" className="flex-1">
            Apply filter
          </Button>
        </div>
      </Card>
    </Form>
  );

  useEffect(() => {
    getFilterFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      placement="bottomRight"
      open={open.value}
      onOpenChange={(visible) => open.onToggle()}
    >
      <Badge dot={!isEmpty}>
        <Tooltip overlay="Filter">
          <Button icon={<FilterOutlined />} type="primary"></Button>
        </Tooltip>
      </Badge>
    </Dropdown>
  );
};

export default memo(Filter);
